import { makeStyles, TextareaAutosize } from '@material-ui/core'
import PropTypes from 'prop-types'
import React, { forwardRef, useCallback, useImperativeHandle, useRef } from 'react'
import Icon from '../../../../../atoms/Icon'
import SydButton from '../../../../../commonDesign/Button'
import FadeIn from '../../../../../molecules/Transitions/FadeIn'

const useStyles = makeStyles(() => ({
  inputWrapper: {
    border: '1px solid var(--color-neutral-600)',
    transition: 'border-color 0.2s ease',
    '&:focus-within': {
      borderColor: 'var(--color-primary-500)'
    }
  },
  textarea: {
    backgroundColor: 'var(--color-neutral-050)',
    border: 'none',
    outline: 'none',
    resize: 'none',
    lineHeight: 1.6,
    color: 'var(--font-color-main)',
    fontFamily: 'inherit',
    '&::placeholder': {
      color: 'var(--color-neutral-500)'
    }
  }
}))

const MAX_LENGTH = 4000

const MessageInput = forwardRef(({ onSend, disabled, placeholder, isProcessing, onCancel, isCancelling }, ref) => {
  const classes = useStyles()
  const textareaRef = useRef(null)
  const [value, setValue] = React.useState('')
  const [charCount, setCharCount] = React.useState(0)
  const [hasContent, setHasContent] = React.useState(false)

  useImperativeHandle(ref, () => ({
    setValue: (newValue) => {
      const trimmedValue = newValue.slice(0, MAX_LENGTH)
      setValue(trimmedValue)
      setCharCount(trimmedValue.length)
      setHasContent(!!trimmedValue.trim())
      if (textareaRef.current) {
        textareaRef.current.focus()
        textareaRef.current.setSelectionRange(trimmedValue.length, trimmedValue.length)
      }
    },
    focus: () => textareaRef.current?.focus(),
    clear: () => { setValue(''); setCharCount(0); setHasContent(false) }
  }), [])

  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      if (value.trim() && !disabled && !isProcessing) {
        onSend(value)
        setValue('')
        setCharCount(0)
        setHasContent(false)
      }
    }
  }, [value, disabled, isProcessing, onSend])

  const handleSubmit = useCallback(() => {
    if (value.trim() && !disabled) {
      onSend(value)
      setValue('')
      setCharCount(0)
      setHasContent(false)
    }
  }, [value, disabled, onSend])

  const handleChange = useCallback((e) => {
    const newValue = e.target.value.slice(0, MAX_LENGTH)
    setValue(newValue)
    setCharCount(newValue.length)
    setHasContent(!!newValue.trim())
  }, [])

  const remainingChars = MAX_LENGTH - charCount
  const isNearLimit = remainingChars < 100

  return (
    <FadeIn className='py-1 px-2'>
      <div className='w-full mx-auto' style={{ maxWidth: '1200px' }}>
        <div className={`${classes.inputWrapper} flex flex-col gap-3 bg-neutral-050 shadow-sm radius-lg py-3 px-4`}>
          <TextareaAutosize
            ref={textareaRef}
            className={`${classes.textarea} w-full font-size-sm`}
            placeholder={placeholder}
            value={value}
            onChange={handleChange}
            onKeyDown={handleKeyDown}
            minRows={1}
            maxRows={10}
          />
          <div className='flex justify-between items-center'>
            <span className='font-size-xs' style={{ color: isNearLimit ? 'var(--color-warning-500)' : 'var(--color-neutral-500)' }}>
              {remainingChars}
            </span>
            <SydButton
              size='sm'
              variant={isProcessing ? 'outline' : 'ai'}
              onClick={isProcessing ? onCancel : handleSubmit}
              disabled={isProcessing ? isCancelling : (disabled || !hasContent)}
            >
              <Icon
                name={isProcessing ? 'square' : 'navigateNext'}
                style={{ fontSize: '18px', fill: isProcessing ? undefined : 'var(--color-contrast-ai-500)' }}
              />
            </SydButton>
          </div>
        </div>
      </div>
    </FadeIn>
  )
})

MessageInput.displayName = 'MessageInput'

MessageInput.propTypes = {
  onSend: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  placeholder: PropTypes.string,
  isProcessing: PropTypes.bool,
  onCancel: PropTypes.func,
  isCancelling: PropTypes.bool
}

MessageInput.defaultProps = {
  disabled: false,
  placeholder: 'Type a message...'
}

export default MessageInput
